export class HttpError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

// FastAPI returns errors as {"detail": "..."} (string for our handcrafted
// HTTPExceptions, list-of-dicts for Pydantic validation). Pull the human
// part so toasts/banners don't render raw JSON. Never throw from the parser
// itself — it runs in the error path and any failure should fall through to
// the raw body so we don't lose the signal entirely.
function extractErrorMessage(status: number, statusText: string, body: string, contentType: string): string {
  if (contentType.includes('application/json') && body) {
    try {
      const parsed = JSON.parse(body);
      const d = parsed?.detail;
      if (typeof d === 'string' && d.length > 0) return d;
      if (Array.isArray(d) && d.length > 0) {
        // Pydantic validation: [{loc, msg, type}, ...]. Take the first msg.
        const first = d[0];
        if (first && typeof first.msg === 'string') return first.msg;
      }
    } catch {
      // Body wasn't actually JSON despite the content-type — fall through.
    }
  }
  return body || `${status} ${statusText}`;
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  const r = await fetch(url, {
    method,
    headers: body ? { 'content-type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
    credentials: 'same-origin'
  });
  if (!r.ok) {
    // A 401 means the session was lost mid-use. Bounce to /login immediately
    // so React Query doesn't retry and produce more 401s — fail2ban watches
    // those, and we don't want a stale tab to trip the jail.
    if (r.status === 401 && !location.pathname.startsWith('/login')) {
      location.href = '/login';
    }
    const text = await r.text();
    const ct = r.headers.get('content-type') ?? '';
    throw new HttpError(r.status, extractErrorMessage(r.status, r.statusText, text, ct));
  }
  if (r.status === 204) return undefined as T;
  const ct = r.headers.get('content-type') ?? '';
  if (ct.includes('application/json')) return r.json();
  return undefined as T;
}

export const api = {
  get: <T>(url: string) => request<T>('GET', url),
  post: <T>(url: string, body?: unknown) => request<T>('POST', url, body ?? {}),
  put: <T>(url: string, body?: unknown) => request<T>('PUT', url, body ?? {}),
  patch: <T>(url: string, body?: unknown) => request<T>('PATCH', url, body ?? {}),
  delete: <T>(url: string) => request<T>('DELETE', url)
};
